import { requiredFeaturesCompanyLevel } from "@utils/feature-control/company/company-feature-control-helper";
import {
  HasFeatureCompanyLevelProps,
  HasUserValidFeatureCompanyLevelProps,
  RequiredFeatureCompanyLevelName,
  RequiredFeaturesCompanyLevel,
} from "@utils/feature-control/company/company-feature-control-types";

/**
 * Checks whether a feature is enabled and permitted to use by the user within the company level.
 *
 * @returns True if the feature is enabled and permitted within company level.
 */
export function hasUserValidFeatureCompanyLevel<
  FeatureNameT extends string = RequiredFeatureCompanyLevelName
>({
  featureName,
  featuresAvailable,
  defaultRequiredFeaturesCompanyLevel = requiredFeaturesCompanyLevel as RequiredFeaturesCompanyLevel<FeatureNameT>,
}: HasUserValidFeatureCompanyLevelProps<FeatureNameT>): boolean {
  // Checks if the user has the required features roles for the company
  if (
    defaultRequiredFeaturesCompanyLevel[featureName].companySubscriptionRoles &&
    hasFeatureCompanyLevel({
      featuresAvailable: featuresAvailable ?? null,
      requiredCompanyFeatures:
        defaultRequiredFeaturesCompanyLevel[featureName].companySubscriptionRoles,
    })
  ) {
    return true;
  }

  // If the user didn't match any of the rules above it means it
  // does not have permission.
  return false;
}

/**
 * Checks whether required features are available for the company level in featuresAvailable list.
 * This required "enabled" and "permitted" both property to be true
 * @returns True if the company has required subscription.
 */
function hasFeatureCompanyLevel({
  featuresAvailable,
  requiredCompanyFeatures,
}: HasFeatureCompanyLevelProps): boolean {
  // Early return if there is no available features provided
  if (!featuresAvailable) {
    return false;
  }

  /**
   * Early return if there is no required company features
   * In this case there is no required features
   */
  if (!requiredCompanyFeatures.length) {
    return true;
  }

  if (featuresAvailable && featuresAvailable.length > 0) {
    return requiredCompanyFeatures.every((requiredCompanyFeature) => {
      const feature = featuresAvailable.find(
        (feature) => feature.identifier === requiredCompanyFeature
      );
      if (feature && feature.enabled && feature.permitted) {
        return true;
      }
      return false;
    });
  }

  // Returning false if no criteria met
  return false;
}

/**
 * Checks whether a user has permission to use a feature within the company level.
 * This required "permitted" property to be true
 *
 * @returns True if the user has a valid company level permission.
 */
export function isUserPermittedToFeatureCompanyLevel<
  FeatureNameT extends string = RequiredFeatureCompanyLevelName
>({
  featureName,
  featuresAvailable,
  defaultRequiredFeaturesCompanyLevel = requiredFeaturesCompanyLevel as RequiredFeaturesCompanyLevel<FeatureNameT>,
}: HasUserValidFeatureCompanyLevelProps<FeatureNameT>): boolean {
  // Checks if the user has the required features roles for the company
  if (
    defaultRequiredFeaturesCompanyLevel[featureName].companySubscriptionRoles &&
    hasFeaturePermissionCompanyLevel({
      featuresAvailable: featuresAvailable ?? null,
      requiredCompanyFeatures:
        defaultRequiredFeaturesCompanyLevel[featureName].companySubscriptionRoles,
    })
  ) {
    return true;
  }

  // If the user didn't match any of the rules above it means it
  // does not have permission.
  return false;
}

/**
 * Checks whether required features are permitted to use in the company level.
 * This required "permitted" property to be true
 *
 * @returns True if the company has required subscription permitted.
 */
function hasFeaturePermissionCompanyLevel({
  featuresAvailable,
  requiredCompanyFeatures,
}: HasFeatureCompanyLevelProps): boolean {
  // Early return if there is no available features provided
  if (!featuresAvailable) {
    return false;
  }

  /**
   * Early return if there is no required company features
   * In this case there is no required features
   */
  if (!requiredCompanyFeatures.length) {
    return true;
  }

  if (featuresAvailable && featuresAvailable.length > 0) {
    return requiredCompanyFeatures.every((requiredCompanyFeature) => {
      const feature = featuresAvailable.find(
        (feature) => feature.identifier === requiredCompanyFeature
      );
      if (feature && feature.permitted) {
        return true;
      }
      return false;
    });
  }

  // Returning false if no criteria met
  return false;
}
